/**
 * QuestAPI — API client for quest endpoints
 */

import { loadAccessToken } from './NetworkManager';

const SERVER_URL = import.meta.env.VITE_SERVER_URL as string || (
  typeof window !== 'undefined' && window.location.hostname !== 'localhost'
    ? `${window.location.protocol}//${window.location.host}`
    : 'http://localhost:8080'
);

// --- Types ---

export interface PlayerQuestView {
  id: string;
  quest_id: string;
  type: 'daily' | 'weekly';
  title: string;
  description: string;
  condition: string;
  target: number;
  param?: string;
  progress: number;
  completed: boolean;
  claimed: boolean;
  reward_coins: number;
  reward_dust: number;
  reward_packs: number;
  assigned_at: string;
  claimed_at?: string;
}

export interface QuestRewardResult {
  quest_id: string;
  coins_earned: number;
  dust_earned: number;
  packs_earned: number;
  total_coins: number;
}

// --- Helpers ---

class QuestApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'QuestApiError';
    this.status = status;
  }
}

async function questRequest<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = loadAccessToken();
  if (!token) {
    throw new QuestApiError(401, '未登入');
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };

  const res = await fetch(`${SERVER_URL}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  const data: unknown = await res.json();

  if (!res.ok) {
    const errorData = data as { error?: string };
    throw new QuestApiError(res.status, errorData.error ?? '未知錯誤');
  }

  return data as T;
}

// --- Quest APIs ---

/** Get current daily + weekly quests (auto-assigns if needed). */
export async function getQuests(): Promise<PlayerQuestView[]> {
  return questRequest<PlayerQuestView[]>('GET', '/api/quests');
}

/** Claim a completed quest's reward. */
export async function claimReward(questId: string): Promise<QuestRewardResult> {
  return questRequest<QuestRewardResult>('POST', '/api/quests/claim', { quest_id: questId });
}
